import React, { useEffect, useRef, useCallback, useState } from "react";

export type WhiteboardTool = "pen" | "highlight" | "eraser";

export interface WhiteboardStroke {
  type: "stroke" | "highlight";
  color: string;
  width: number;
  points: Array<{ x: number; y: number }>;
}

interface WhiteboardCanvasProps {
  tool: WhiteboardTool;
  color: string;
  width: number;
  /** Background color the eraser paints with (must match the canvas backdrop). */
  background?: string;
  /** Eraser stroke width multiplier (eraser is fatter than pen). */
  eraserWidthMultiplier?: number;
  /** Read-only mode (used by the student-side replay). */
  readOnly?: boolean;
  onStrokeComplete?: (stroke: WhiteboardStroke) => void;
  /**
   * Imperative controls: parent uses these to clear or undo when broadcast
   * events arrive (student side) or when the faculty hits Clear/Undo buttons.
   */
  registerControls?: (controls: {
    clearAll: () => void;
    popLastStroke: () => void;
    applyRemoteStroke: (stroke: WhiteboardStroke) => void;
  }) => void;
}

/**
 * Imperative HTML5 canvas — pen, highlighter, eraser.
 * Coordinates are normalized (0..1) so strokes replay correctly on any screen.
 *
 * On pointerup, fires onStrokeComplete with the normalized stroke. Caller
 * batches that to the server (one POST per pen lift, not per point).
 *
 * Eraser is implemented as a stroke painted with the background color — that
 * way it broadcasts through the same channel as ink strokes (no special case).
 */
const WhiteboardCanvas: React.FC<WhiteboardCanvasProps> = ({
  tool,
  color,
  width,
  background = "var(--surface-card)",
  eraserWidthMultiplier = 6,
  readOnly = false,
  onStrokeComplete,
  registerControls,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const currentStrokeRef = useRef<Array<{ x: number; y: number }>>([]);
  const strokesRef = useRef<WhiteboardStroke[]>([]);
  const [, forceRedraw] = useState(0);

  const toolRef = useRef(tool);
  const colorRef = useRef(color);
  const widthRef = useRef(width);
  useEffect(() => { toolRef.current = tool; }, [tool]);
  useEffect(() => { colorRef.current = color; }, [color]);
  useEffect(() => { widthRef.current = width; }, [width]);

  const redrawAll = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const stroke of strokesRef.current) {
      drawStroke(ctx, canvas.width, canvas.height, stroke);
    }
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    redrawAll();
  }, [redrawAll]);

  useEffect(() => {
    resizeCanvas();
    const handleResize = () => resizeCanvas();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [resizeCanvas]);

  useEffect(() => {
    if (!registerControls) return;
    registerControls({
      clearAll: () => {
        strokesRef.current = [];
        redrawAll();
        forceRedraw((t) => t + 1);
      },
      popLastStroke: () => {
        strokesRef.current.pop();
        redrawAll();
        forceRedraw((t) => t + 1);
      },
      applyRemoteStroke: (stroke) => {
        strokesRef.current.push(stroke);
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (canvas && ctx) drawStroke(ctx, canvas.width, canvas.height, stroke);
      },
    });
  }, [registerControls, redrawAll]);

  const getNormalizedPoint = (e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (readOnly) return;
    drawingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    currentStrokeRef.current = [getNormalizedPoint(e)];
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    const pt = getNormalizedPoint(e);
    currentStrokeRef.current.push(pt);

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || currentStrokeRef.current.length < 2) return;
    const last = currentStrokeRef.current[currentStrokeRef.current.length - 2];
    if (!last) return;
    const { renderType, renderColor, renderWidth } = resolveRenderStyle(
      toolRef.current,
      colorRef.current,
      widthRef.current,
      background,
      eraserWidthMultiplier,
    );
    drawSegment(ctx, canvas.width, canvas.height, last, pt, renderType, renderColor, renderWidth);
  };

  const handlePointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const pts = currentStrokeRef.current;
    currentStrokeRef.current = [];
    if (pts.length < 2) return;
    const { renderType, renderColor, renderWidth } = resolveRenderStyle(
      toolRef.current,
      colorRef.current,
      widthRef.current,
      background,
      eraserWidthMultiplier,
    );
    const stroke: WhiteboardStroke = {
      type: renderType,
      color: renderColor,
      width: renderWidth,
      points: pts,
    };
    strokesRef.current.push(stroke);
    onStrokeComplete?.(stroke);
  };

  return (
    <div ref={containerRef} className="wb-canvas-wrap">
      <canvas
        ref={canvasRef}
        className="wb-canvas"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{ touchAction: "none", cursor: readOnly ? "default" : "crosshair" }}
      />
    </div>
  );
};

function resolveRenderStyle(
  tool: WhiteboardTool,
  color: string,
  width: number,
  background: string,
  eraserMultiplier: number,
) {
  if (tool === "eraser") {
    return {
      renderType: "stroke" as const,
      renderColor: background,
      renderWidth: width * eraserMultiplier,
    };
  }
  return {
    renderType: tool === "highlight" ? ("highlight" as const) : ("stroke" as const),
    renderColor: color,
    renderWidth: width,
  };
}

// ── Drawing helpers ─────────────────────────────────────────────────────

export function drawStroke(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  stroke: WhiteboardStroke,
): void {
  const pts = stroke.points;
  if (pts.length < 2) return;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    drawSegment(ctx, w, h, a, b, stroke.type, stroke.color, stroke.width);
  }
}

function drawSegment(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  a: { x: number; y: number },
  b: { x: number; y: number },
  type: "stroke" | "highlight",
  color: string,
  width: number,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(a.x * w, a.y * h);
  ctx.lineTo(b.x * w, b.y * h);
  ctx.strokeStyle = color;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (type === "highlight") {
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = width * 3;
  } else {
    ctx.globalAlpha = 1;
    ctx.lineWidth = width;
  }
  ctx.stroke();
  ctx.restore();
}

export default WhiteboardCanvas;
